import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Edit, Trash2, Upload, Users, Mail, QrCode, Copy, ExternalLink, Download, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { getEvents, createEvent, updateEvent, deleteEvent, uploadFile, getEventRegistrations, getImageUrl } from '../../utils/api';
import { toast } from 'sonner';
import './AdminStyles.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showRegistrations, setShowRegistrations] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [sendingReminders, setSendingReminders] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    city: '',
    venue: '',
    date: '',
    time: '',
    image: '',
    hero_image: '',
    lineup: [],
    description: '',
    registration_enabled: true,
    reminder_days: 1,
    check_in_pin: ''
  });
  const [uploading, setUploading] = useState(false);
  const [lineupInput, setLineupInput] = useState('');
  const [currentEventId, setCurrentEventId] = useState(null);
  const [importingCsv, setImportingCsv] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const fileInputRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Handle scroll indicator visibility
  const handleTableScroll = useCallback((e) => {
    const container = e.target;
    const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 5;
    container.classList.toggle('scrolled-end', isAtEnd);
    setShowScrollIndicator(!isAtEnd);
  }, []);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleTableScroll);
      // Check initial state
      const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 5;
      setShowScrollIndicator(!isAtEnd);
      return () => container.removeEventListener('scroll', handleTableScroll);
    }
  }, [events, handleTableScroll]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const data = await getEvents();
      setEvents(data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async (eventId) => {
    try {
      const data = await getEventRegistrations(eventId);
      setRegistrations(data);
      setCurrentEventId(eventId);
      setShowRegistrations(true);
    } catch (error) {
      toast.error('Failed to load registrations');
    }
  };

  const handleFileUpload = async (e, field = 'image') => {
    console.log('File upload triggered for', field);
    const file = e.target?.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('Uploading file:', file.name);
    setUploading(true);
    
    try {
      const result = await uploadFile(file);
      console.log('Upload result:', result);
      setFormData(prev => ({ ...prev, [field]: result.url }));
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to upload image. Please try again.');
      }
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const eventData = {
      ...formData,
      lineup: lineupInput.split(',').map(a => a.trim()).filter(Boolean)
    };

    try {
      if (editingEvent) {
        await updateEvent(editingEvent.id, eventData);
        toast.success('Event updated');
      } else {
        await createEvent(eventData);
        toast.success('Event created');
      }
      fetchEvents();
      closeModal();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const handleSendReminders = async (eventId) => {
    setSendingReminders(eventId);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/api/admin/events/${eventId}/send-reminders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Sending reminders to ${data.sent} attendees!`);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to send reminders');
      }
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(null);
    }
  };

  const copyCheckInLink = (event) => {
    const link = `${window.location.origin}/events/${event.slug || event.id}/check-in`;
    navigator.clipboard.writeText(link);
    toast.success('Check-in link copied!');
  };

  const copySelfCheckInLink = (event) => {
    const link = `${window.location.origin}/events/${event.slug || event.id}/register`;
    navigator.clipboard.writeText(link);
    toast.success('Self check-in link copied!');
  };

  const handleGenerateQRCodes = async (eventId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/api/admin/events/${eventId}/registrations/generate-qrcodes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to generate QR codes');
      }
    } catch (error) {
      toast.error('Failed to generate QR codes');
    }
  };

  const handleExportCsv = async (eventId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/events/${eventId}/registrations/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `registrations_${eventId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('CSV exported successfully!');
      } else {
        toast.error('Failed to export CSV');
      }
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleImportCsv = async (eventId, file) => {
    if (!file) return;
    
    setImportingCsv(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/admin/events/${eventId}/registrations/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Imported ${data.imported} registrations (${data.skipped} skipped)`);
        fetchRegistrations(eventId);
      } else {
        toast.error(data.detail || 'Failed to import CSV');
      }
    } catch (error) {
      toast.error('Failed to import CSV');
    } finally {
      setImportingCsv(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      location: event.location,
      city: event.city,
      venue: event.venue,
      date: event.date,
      time: event.time,
      image: event.image || '',
      hero_image: event.hero_image || '',
      lineup: event.lineup || [],
      description: event.description || '',
      registration_enabled: event.registration_enabled !== false,
      reminder_days: event.reminder_days || 1,
      check_in_pin: event.check_in_pin || ''
    });
    setLineupInput(event.lineup?.join(', ') || '');
    setShowModal(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await deleteEvent(eventId);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const closeModal = () => {
    console.log('closeModal called');
    setShowModal(false);
    setEditingEvent(null);
    setFormData({
      title: '',
      location: '',
      city: '',
      venue: '',
      date: '',
      time: '',
      image: '',
      hero_image: '',
      lineup: [],
      description: '',
      registration_enabled: true,
      reminder_days: 1,
      check_in_pin: ''
    });
    setLineupInput('');
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Event Management</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={20} />
          Add Event
        </button>
      </div>

      <div className="table-scroll-wrapper">
        <div className="admin-table-container" ref={tableContainerRef}>
          {events.length === 0 && (
            <div style={{padding: 'var(--spacing-xl)', textAlign: 'center', color: 'var(--light-grey)'}}>
              No events yet. Click "Add Event" to create one.
            </div>
          )}
          {events.length > 0 && (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Location</th>
                  <th>Date</th>
                  <th>Registration</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => {
                  return (
                    <tr key={event.id}>
                      <td>{event.title}</td>
                      <td>{event.venue}, {event.city}</td>
                      <td>{new Date(event.date).toLocaleDateString()}</td>
                      <td>
                        {event.registration_enabled ? (
                          <span className="status-badge enabled">Enabled</span>
                        ) : (
                          <span className="status-badge disabled">Disabled</span>
                        )}
                        {event.check_in_pin && (
                          <span className="status-badge pin" title={`PIN: ${event.check_in_pin}`}>
                            <QrCode size={12} /> PIN Set
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="table-actions">
                          <button className="action-btn" onClick={() => fetchRegistrations(event.id)}>
                            <Users size={16} /> Registrations
                          </button>
                          <button className="action-btn" onClick={() => copySelfCheckInLink(event)} title="Copy self check-in link (public)">
                            <ExternalLink size={16} /> Self Check-In
                          </button>
                          {event.check_in_pin && (
                            <button className="action-btn" onClick={() => copyCheckInLink(event)} title="Copy staff check-in link (PIN protected)">
                              <QrCode size={16} /> Staff Check-In
                            </button>
                          )}
                          <button 
                            className="action-btn" 
                            onClick={() => handleSendReminders(event.id)}
                            disabled={sendingReminders === event.id}
                            title="Send reminder emails to all registrants"
                          >
                            <Mail size={16} /> {sendingReminders === event.id ? 'Sending...' : 'Remind'}
                          </button>
                          <button className="action-btn" onClick={() => handleEdit(event)}>
                            <Edit size={16} /> Edit
                          </button>
                          <button className="action-btn delete" onClick={() => handleDelete(event.id)}>
                            <Trash2 size={16} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {showScrollIndicator && events.length > 0 && (
          <div className="table-scroll-hint">
            <span className="table-scroll-arrow">→</span>
          </div>
        )}
      </div>

      {/* Event Form Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingEvent ? 'Edit Event' : 'Add Event'}</h2>
              <button className="close-btn" onClick={closeModal} type="button">×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form" onReset={(e) => e.preventDefault()}>
              <div className="form-group">
                <label>Event Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Venue *</label>
                  <input
                    type="text"
                    value={formData.venue}
                    onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Full Location *</label>
                <input
                  type="text"
                  placeholder="e.g., Toronto, Canada"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Time *</label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Lineup (comma-separated)</label>
                <input
                  type="text"
                  placeholder="e.g., Artist 1, Artist 2, Artist 3"
                  value={lineupInput}
                  onChange={(e) => setLineupInput(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Event Image</label>
                <span className="dimension-hint">Recommended: 800 x 600px (landscape)</span>
                <div className="file-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'image')}
                    id="event-image-upload"
                    className="hidden-file-input"
                  />
                  <button
                    type="button"
                    className="upload-btn"
                    onClick={() => document.getElementById('event-image-upload').click()}
                  >
                    <Upload size={20} />
                    {uploading ? 'Uploading...' : 'Upload Image'}
                  </button>
                  {formData.image && (
                    <img src={getImageUrl(formData.image)} alt="Preview" className="image-preview" />
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Hero Background Image</label>
                <span className="dimension-hint">Recommended: 1920 x 600px (wide banner)</span>
                <div className="file-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'hero_image')}
                    id="event-hero-image-upload"
                    className="hidden-file-input"
                  />
                  <button
                    type="button"
                    className="upload-btn"
                    onClick={() => document.getElementById('event-hero-image-upload').click()}
                  >
                    <Upload size={20} />
                    Upload Hero Image
                  </button>
                  {formData.hero_image && (
                    <img src={getImageUrl(formData.hero_image)} alt="Hero Preview" className="image-preview" />
                  )}
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.registration_enabled}
                    onChange={(e) => setFormData({ ...formData, registration_enabled: e.target.checked })}
                  />
                  Enable Registration
                </label>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Reminder Email (days before)</label>
                  <select
                    value={formData.reminder_days}
                    onChange={(e) => setFormData({ ...formData, reminder_days: parseInt(e.target.value) })}
                  >
                    <option value={1}>1 day before</option>
                    <option value={3}>3 days before</option>
                    <option value={7}>7 days before</option>
                    <option value={14}>14 days before</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Check-In PIN (for door access)</label>
                  <input
                    type="text"
                    placeholder="e.g., 1234"
                    maxLength={6}
                    value={formData.check_in_pin}
                    onChange={(e) => setFormData({ ...formData, check_in_pin: e.target.value })}
                  />
                  <span className="input-hint">PIN protects the check-in page</span>
                </div>
              </div>

              {editingEvent && formData.check_in_pin && (
                <div className="check-in-link-box">
                  <label>Check-In Page Link:</label>
                  <div className="link-display">
                    <code>{window.location.origin}/events/{editingEvent.slug}/check-in</code>
                    <button 
                      type="button" 
                      className="btn btn-sm"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/events/${editingEvent.slug}/check-in`);
                        toast.success('Link copied!');
                      }}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingEvent ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Registrations Modal */}
      {showRegistrations && (
        <div className="modal-overlay">
          <div className="modal-content modal-wide">
            <div className="modal-header">
              <h2>Event Registrations ({registrations.length})</h2>
              <button className="close-btn" onClick={() => { setShowRegistrations(false); setCurrentEventId(null); }}>×</button>
            </div>
            
            {/* CSV Import/Export Actions */}
            <div className="registrations-actions">
              <button 
                className="btn btn-primary"
                onClick={() => handleExportCsv(currentEventId)}
                disabled={registrations.length === 0}
              >
                <Download size={16} /> Export CSV
              </button>
              
              <label className="btn btn-secondary import-btn">
                <Upload size={16} /> {importingCsv ? 'Importing...' : 'Import CSV'}
                <input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={(e) => handleImportCsv(currentEventId, e.target.files[0])}
                  style={{ display: 'none' }}
                  disabled={importingCsv}
                />
              </label>
              
              <button 
                className="btn btn-secondary"
                onClick={() => handleGenerateQRCodes(currentEventId)}
                title="Generate QR codes for registrations that don't have one"
              >
                <RefreshCw size={16} /> Generate QR Codes
              </button>
            </div>
            
            <div className="registrations-table-wrapper">
              {registrations.length === 0 && (
                <p style={{color: 'var(--light-grey)', padding: 'var(--spacing-lg)'}}>No registrations yet</p>
              )}
              {registrations.length > 0 && (
                <table className="admin-table registrations-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Newsletter</th>
                      <th>Checked In</th>
                      <th>Registered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map((reg) => {
                      return (
                        <tr key={reg.id} className={reg.checked_in ? 'checked-in-row' : ''}>
                          <td>{reg.name}</td>
                          <td>{reg.email}</td>
                          <td>{reg.phone || '-'}</td>
                          <td>
                            {reg.subscribe_newsletter !== false ? (
                              <CheckCircle size={16} className="icon-yes" />
                            ) : (
                              <XCircle size={16} className="icon-no" />
                            )}
                          </td>
                          <td>
                            {reg.checked_in ? (
                              <span className="status-badge enabled">
                                <CheckCircle size={12} /> Yes
                              </span>
                            ) : (
                              <span className="status-badge disabled">No</span>
                            )}
                          </td>
                          <td>{new Date(reg.registered_at).toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;
